import type { KerningOverrides } from "./types";

// Wraps the left character of each kerned pair in a <span style="letter-spacing">.
// Identity transform when overrides is empty/absent — production designs render
// byte-identically. <br> bumps the index counter (mirrors plainTextFromHTML).
//
// baseLetterSpacing (thousandths of em, matching the elementData.letterSpacing
// scale) is added to each override so the rendered span overrides the parent's
// letter-spacing with (base + override) rather than just the override delta.
// This way kerning quickkeys nudge a pair relative to the global tracking
// instead of clobbering it: LS=800 + Opt+Right(+20) renders the pair at 820.
export const applyKerningOverrides = (
  html: string,
  overrides: KerningOverrides | null | undefined,
  baseLetterSpacing: number = 0
): string => {
  if (!overrides) return html;
  if (Object.keys(overrides).length === 0) return html;
  if (!html) return html;

  // RangeInput's onChange forwards e.target.value (a string) into the live
  // elementPreviews slice, so during slider drag this lands here as a string.
  // Coerce so + below stays arithmetic instead of string-concatenating.
  const base = Number(baseLetterSpacing) || 0;

  const tmp = document.createElement("div");
  tmp.innerHTML = html;

  let idx = 0;
  let didWrap = false;

  const wrapChar = (
    textNode: Text,
    localTargets: Array<{ pos: number; value: number }>
  ) => {
    const text = textNode.nodeValue ?? "";
    const parent = textNode.parentNode;
    if (!parent) return;
    const fragments: Node[] = [];
    let cursor = 0;
    for (const { pos, value } of localTargets) {
      if (pos > cursor) {
        fragments.push(document.createTextNode(text.slice(cursor, pos)));
      }
      const span = document.createElement("span");
      span.dataset.kerning = "1";
      span.style.letterSpacing = `${(base + value) / 1000}em`;
      span.textContent = text[pos] ?? "";
      fragments.push(span);
      cursor = pos + 1;
    }
    if (cursor < text.length) {
      fragments.push(document.createTextNode(text.slice(cursor)));
    }
    for (const frag of fragments) {
      parent.insertBefore(frag, textNode);
    }
    parent.removeChild(textNode);
  };

  const walk = (node: Node): void => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.nodeValue ?? "";
        const localTargets: Array<{ pos: number; value: number }> = [];
        for (let i = 0; i < text.length; i++) {
          const value = overrides[idx + i];
          if (typeof value === "number" && Number.isFinite(value) && value !== 0) {
            localTargets.push({ pos: i, value });
          }
        }
        if (localTargets.length > 0) {
          wrapChar(child as Text, localTargets);
          didWrap = true;
        }
        idx += text.length;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if ((child as Element).nodeName === "BR") {
          idx += 1;
        } else {
          walk(child);
        }
      }
    }
  };

  walk(tmp);

  if (!didWrap) return html;
  return tmp.innerHTML;
};

export const wrapKerningOverridesHTML = applyKerningOverrides;
